import { useState } from 'react'
import InputField from '../inputField'
import Dropdown, { type selectOption } from '#app/components/common/dropdown'
import clsx from 'clsx'
import { chainOptions } from '#app/utils/misc'
import { CollectionInputDataTypes } from './collectionInputModal'

export type TokenRangeInputData = {
	contract: string
	tokenStart: string
	tokenEnd: string
	chain: string
	type: 'smartContractRange'
	inputSelector: string
}

// FILTER OUT SOLANA BECAUSE IT DOESNT SUPPORT SEQUENTIAL TOKENS
export const chainSelectionOptions = chainOptions.filter(
	chain => chain.name !== 'Solana',
)

const TokenRangeInput = ({
	name = 'CollectionNFTs',
	id,
	slug,
	onChange,
	errorFields,
	inputData,
	isInactive = false,
}: {
	name: string
	onChange: (val: CollectionInputDataTypes, key?: string) => void
	id: string
	slug: string
	isInactive?: boolean
	inputData?: TokenRangeInputData
	errorFields?: { [key: string]: string }
}) => {
	const [selectedChain, setSelectedChain] = useState<selectOption | null>(
		inputData && inputData.chain
			? chainSelectionOptions.find(
					chainOption => chainOption.slug === inputData.chain,
				) || null
			: null,
	)
	const [contractAddress, setContractAddress] = useState(
		inputData && inputData.contract ? inputData.contract : '',
	)
	const [tokenStart, setTokenStart] = useState(
		inputData && inputData.tokenStart ? inputData.tokenStart : '',
	)
	const [tokenEnd, setTokenEnd] = useState(
		inputData && inputData.tokenEnd ? inputData.tokenEnd : '',
	)

	const handleChainChange = (selected: selectOption) => {
		setSelectedChain(selected)
		onChange(
			{
				contract: contractAddress,
				chain: selected?.slug || '',
				type: 'smartContractRange' as TokenRangeInputData['type'],
				inputSelector: `nfts-${id}-${slug}-tokenRange`,
				tokenStart: tokenStart,
				tokenEnd: tokenEnd,
			},
			'chain',
		)
	}

	const handleChange = (key: string, val: string) => {
		if (key === 'contract') {
			setContractAddress(val)
		} else if (key === 'tokenStart') {
			setTokenStart(val)
		} else if (key === 'tokenEnd') {
			setTokenEnd(val)
		}
		onChange(
			{
				contract: key === 'contract' ? val : contractAddress,
				chain: selectedChain?.slug || '',
				type: 'smartContractRange' as TokenRangeInputData['type'],
				inputSelector: `nfts-${id}-${slug}-tokenRange`,
				tokenStart: key === 'tokenStart' ? val : tokenStart,
				tokenEnd: key === 'tokenEnd' ? val : tokenEnd,
			},
			key,
		)
	}

	return (
		<div className={clsx('flex w-full flex-col')}>
			<Dropdown
				containerClasses="mb-2"
				additionalClasses={clsx(
					'h-12',
					errorFields && errorFields.chain ? 'border-red dark:border-red' : '',
				)}
				options={chainSelectionOptions}
				title="Select a Chain"
				placeholder="Select a Chain"
				selected={selectedChain}
				onSelectionChange={(selection: any) => handleChainChange(selection)}
			/>
			<input
				type="string"
				name={`nfts-${id}-${slug}-chain`}
				id={`nfts-${id}-${slug}-chain`}
				hidden={true}
				readOnly={true}
				value={selectedChain ? selectedChain.slug : ''}
			/>
			<InputField
				name="Contract Address"
				type="string"
				id={`nfts-${id}-${slug}-contract`}
				placeholder="Enter Contract Address"
				containerWidth="100%"
				displayName={true}
				value={contractAddress}
				onChange={e => handleChange('contract', e.currentTarget.value)}
				isError={errorFields && errorFields.contract ? true : false}
				helper={errorFields && errorFields.contract ? errorFields.contract : ''}
			/>
			<div className="my-2 flex flex-col gap-x-0 md:flex-row md:gap-x-2">
				<InputField
					name="Token Start"
					id={`nfts-${id}-${slug}-tokenStart`}
					type="string"
					placeholder="Enter Token Start"
					displayName={true}
					value={tokenStart}
					onChange={e => {
						let newVal = e.currentTarget.value || ''
						newVal = newVal.replace(/[^0-9]/, '')
						handleChange('tokenStart', newVal)
					}}
					helper={
						errorFields && errorFields.tokenStart ? errorFields.tokenStart : ''
					}
					isError={errorFields && errorFields.tokenStart ? true : false}
				/>
				<InputField
					name="Token End"
					id={`nfts-${id}-${slug}-tokenEnd`}
					type="string"
					placeholder="Enter Token End"
					displayName={true}
					value={tokenEnd}
					isError={errorFields && errorFields.tokenEnd ? true : false}
					helper={
						errorFields && errorFields.tokenEnd ? errorFields.tokenEnd : ''
					}
					onChange={e => {
						let newVal = e.currentTarget.value || ''
						newVal = newVal.replace(/[^0-9]/, '')
						handleChange('tokenEnd', newVal)
					}}
				/>
			</div>
		</div>
	)
}

export default TokenRangeInput
